import React, { useRef, useState, useEffect } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import { Formik } from 'formik';
import * as yup from 'yup';
import { setLocale } from 'yup';

import { ICON_SAVE, ICON_CANCEL } from '../../extras/icons';
import ButtonSpinner from '../../layout/ButtonSpinner';

//Redux
import { useDispatch, useSelector } from 'react-redux';
import { updateResultTypesAction, saveResultTypesAction } from '../../../actions/resultTypesActions';
import { listResultModalitiesAction } from '../../../actions/resultModalitiesActions';
//Data
import types from '../../../data/resultTypeGroups';

//Función principal
const ResultTypeForm = props => {
    //Tomo los props
    const { show, handleClose } = props;
    //Genero los refs
    const refDescription = useRef();
    //Datos iniciales
    const initialData = {
        descripcion: '',
        tipo: ''
    }
    //Inicializo el dispatch
    const dispatch = useDispatch();
    //Traigo el store de persona tipos
    const resultTypesLoading = useSelector( state => state.resultTypes.loadingSave );
    const errorSave = useSelector( state => state.resultTypes.errorSave );
    const isEdit = useSelector( state => state.resultTypes.isEdit );
    const resultTypeSelected = useSelector( state => state.resultTypes.resultTypeSelected );
    const resultModalities = useSelector( state => state.resultModalities.resultModalities );
    //Inicializo el state local
    const [ resultType, setResultType ] = useState(initialData);
    //Modifico según el cambio
    useEffect(() => {
        //Me fijo si es edición
        if(isEdit && resultTypeSelected){
            setResultType({
                ...resultTypeSelected,
                tipo: resultTypeSelected.tipoId,
                modalities: resultTypeSelected.modalidadesList && resultTypeSelected.modalidadesList.length > 0 ? 
                    resultTypeSelected.modalidadesList.map(modalidad => ({ value: modalidad.id, label: modalidad.descripcion}))
                 : []
            });
        }else{
            setResultType(initialData);
        }
    }, [isEdit, resultTypeSelected, show])
    //Manejo de cambios
    const handleChange = e => {
        setResultType({
            ...resultType,
            [e.target.name]: e.target.value
        })
    }
    //Manejo cambio del select
    const handleChangeModalities = (options, values) => {
        //Seteo los datos
        setResultType({
            ...values,
            modalities: options ? options.map(option => (
                {
                    id: option.value,
                    value: option.value,
                    label: option.label
                }
            )) : []
        })
    }
    //Manejo el guardado
    const handleSave = values => {
        //Inicializo los datos
        let data = {
            descripcion: values.descripcion,
            tipo: values.tipo,
            modalidadesList: values.modalities,
            activo: 1
        }
        //Verifico si es edición o alta
        if(resultType.id){
            //Agrego el ID a la data
            data = {
                id: resultType.id,
                ...data
            }
            //Es edición
            dispatch( updateResultTypesAction(data, handleCloseForm) );
        }else{
            //Es creación
            dispatch( saveResultTypesAction(data, handleCloseForm) );
        }
    }
    //Manejo cuando se muestra
    const handleOnShow = e => {
        //Me fijo si están las modalidades
        dispatch( listResultModalitiesAction({
            search: 'activo:1',
            sort: 'descripcion:asc'
        }) );
        //Pongo el foco en el primer campo
        refDescription.current.focus();
    }
    //Manejo cierre de la ventana
    const handleCloseForm = e => {
        //Reinicio datos
        setResultType(initialData);
        //Cierro
        handleClose();
    }
    //Esquema de validación
    const validationSchema = yup.object({
        descripcion: yup.string().required(),
        tipo: yup.number().required()
    });
    //Renderizo
    return (
        <Modal 
            show={show} 
            onHide={handleCloseForm} 
            onShow={handleOnShow} 
            aria-labelledby="contained-modal-title-vcenter" 
            centered
            size="lg"
        >
            <Modal.Header closeButton>
                <Modal.Title>
                    Resultado Tipo&nbsp;
                    <small><strong>{ isEdit ? 'Editar' : 'Nuevo' } Registro</strong></small>
                </Modal.Title>
            </Modal.Header>
            <Formik
                enableReinitialize
                validationSchema={validationSchema}
                initialValues={{ ...resultType }}
                onSubmit={ values => handleSave(values) }
            >
                {({
                    handleSubmit,
                    handleChange,
                    handleBlur,
                    values,
                    touched,
                    isValid,
                    errors,
                    isSubmitting
                }) => (
                    <form noValidate onSubmit={handleSubmit} autoComplete="off">
                        <Modal.Body>
                            <div className="row">
                                <Form.Group className="col-md-8">
                                    <label htmlFor="descripcion">Descripción</label>
                                    <Form.Control
                                        id="descripcion"
                                        className="form-control" 
                                        autoFocus 
                                        name="descripcion" 
                                        type="text" 
                                        value={ values.descripcion }
                                        onChange={handleChange}
                                        ref={refDescription}
                                        isInvalid={!!errors.descripcion}
                                        disabled={ resultTypesLoading }
                                    />
                                    <Form.Control.Feedback type="invalid">
                                        { errors.descripcion }
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="col-md-4">
                                    <label htmlFor="tipo">Tipo</label>
                                    <Form.Control
                                        as="select"
                                        id="tipo"
                                        className="form-control"  
                                        name="tipo" 
                                        type="text" 
                                        value={ values.tipo }
                                        onChange={handleChange}
                                        isInvalid={!!errors.tipo}
                                        disabled={ resultTypesLoading }
                                    >
                                        <option value="">Seleccione...</option>
                                        {
                                            types.map(type => (
                                                <option value={type.id}>
                                                    { type.descripcion }
                                                </option>
                                            ))
                                        }
                                    </Form.Control>
                                    <Form.Control.Feedback type="invalid">
                                        { errors.tipo }
                                    </Form.Control.Feedback>
                                </Form.Group>
                                <Form.Group className="col-md-12">
                                    <label htmlFor="modalities">Modalidades</label>
                                    <Select
                                        name="modalities"
                                        onChange={options => handleChangeModalities(options, values)}
                                        isMulti
                                        noOptionsMessage={() => ("No se encontraron datos")}
                                        placeholder="Seleccione"
                                        value={resultType.modalities}
                                        options={
                                            resultModalities ? (
                                                resultModalities.map(resultModality => (
                                                    {
                                                        value: resultModality.id,
                                                        label: resultModality.descripcion
                                                    }
                                                ))
                                            ) : []
                                        }
                                    />
                                </Form.Group>
                            </div>
                        </Modal.Body>
                        <Modal.Footer>
                            {
                                resultTypesLoading ? (
                                    <ButtonSpinner />
                                ) : (
                                    <Button variant="success" type="submit" disabled={ resultTypesLoading }>
                                        <ICON_SAVE /> <strong>{ isEdit ? 'ACTUALIZAR' : 'GUARDAR' }</strong>
                                    </Button>
                                )
                            }
                            
                            <Button variant="secondary" onClick={handleCloseForm} disabled={ resultTypesLoading }>
                                <ICON_CANCEL /> Cancelar
                            </Button>
                        </Modal.Footer>
                    </form>
                )}
            </Formik>
        </Modal>
    );
};

export default ResultTypeForm;